import { alpha, darken, lighten } from "@mui/material/styles";

const CORES_STATUS = {
  ATIVA: "completed",
  EXPIRANDO: "attention",
  EXPIRADA: "inProgress",
  REVOGADA: "pending",
  NAO_PROVISIONADA: "waitingReview",
  NUNCA_EMITIDA: "arquive",
  SEM_LICENCA: "arquive",
};

const useStyles = (theme) => {
  const escuro = theme.palette.mode === "dark";

  const corBase = (status) =>
    theme.palette.chip.main[CORES_STATUS[status] || "arquive"];

  // o chip foi desenhado para fundo sólido; como aqui vira texto sobre o card,
  // precisa de ajuste de luminância para continuar legível nos dois modos
  const corTexto = (status) =>
    escuro ? lighten(corBase(status), 0.2) : darken(corBase(status), 0.2);

  return {
    pageBox: {
      paddingInline: 1,
      paddingTop: 1,
      paddingBottom: 1,
      display: "flex",
      flexDirection: "column",
      height: "calc(100vh - 65px)",
    },
    outerPaper: {
      flexGrow: 1,
      borderRadius: "15px",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      backgroundColor: theme.palette.background.paper,
      padding: 4,
      gap: 2,
    },
    headerBox: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexShrink: 0,
      flexWrap: "wrap",
      gap: 2,
    },
    title: {
      fontWeight: "bold",
      color: theme.palette.text.primary,
    },
    actionsBox: {
      display: "flex",
      gap: 2,
    },
    // dois por linha enquanto couber a faixa de metadados inteira; abaixo disso
    // uma coluna de largura cheia, senão o card quebra em três alturas
    lista: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: "auto",
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      alignContent: "start",
      gap: 2,
      paddingRight: 1,
      "@media (max-width: 1600px)": {
        gridTemplateColumns: "minmax(0, 1fr)",
      },
    },
    card: {
      display: "flex",
      flexDirection: "row",
      borderRadius: "14px",
      overflow: "hidden",
      // no escuro o default é mais escuro que o paper, então clareia em vez de usá-lo
      backgroundColor: escuro
        ? lighten(theme.palette.background.paper, 0.04)
        : theme.palette.background.default,
      border: `1px solid ${theme.palette.divider}`,
      "@media (max-width: 750px)": {
        flexDirection: "column",
      },
    },
    trilho: (status) => ({
      width: "140px",
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: 2,
      backgroundColor: alpha(corBase(status), escuro ? 0.14 : 0.1),
      borderLeft: `4px solid ${corBase(status)}`,
      "@media (max-width: 750px)": {
        width: "100%",
        flexDirection: "row",
        justifyContent: "flex-start",
        gap: 1,
        padding: 1.5,
        borderLeft: "none",
        borderTop: `4px solid ${corBase(status)}`,
      },
    }),
    trilhoValor: (status) => ({
      fontSize: "2.4rem",
      fontWeight: 800,
      lineHeight: 1,
      color: corTexto(status),
    }),
    trilhoUnidade: {
      fontSize: "0.65rem",
      fontWeight: 600,
      letterSpacing: "0.14em",
      color: theme.palette.text.secondary,
      marginTop: "2px",
    },
    trilhoStatus: (status) => ({
      fontSize: "0.65rem",
      fontWeight: 700,
      letterSpacing: "0.12em",
      textAlign: "center",
      color: corTexto(status),
      marginTop: 1.5,
      "@media (max-width: 750px)": {
        marginTop: 0,
      },
    }),
    corpo: {
      flexGrow: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
      padding: 2.5,
    },
    cabecalho: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 1,
    },
    nomeCliente: {
      fontSize: "1.05rem",
      fontWeight: 700,
      color: theme.palette.text.primary,
      minWidth: 0,
      overflowWrap: "anywhere",
    },
    copiarId: {
      flexShrink: 0,
      textTransform: "none",
      fontFamily: "monospace",
      fontSize: "0.7rem",
      lineHeight: 1,
      padding: "4px 10px",
      minWidth: "auto",
      borderRadius: "6px",
      color: theme.palette.text.secondary,
      border: `1px solid ${theme.palette.divider}`,
      "&:hover": {
        backgroundColor: theme.palette.background.hover,
      },
    },
    copiarIdIcone: {
      fontSize: "0.85rem !important",
    },
    metadados: {
      display: "flex",
      flexWrap: "wrap",
      columnGap: 5,
      rowGap: 1.5,
    },
    metadado: {
      display: "flex",
      flexDirection: "column",
      gap: "2px",
      minWidth: "150px",
    },
    rotulo: {
      fontSize: "0.65rem",
      fontWeight: 600,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
    },
    valor: (preenchido) => ({
      fontSize: "0.82rem",
      fontFamily: "monospace",
      color: preenchido
        ? theme.palette.text.primary
        : theme.palette.text.disabled,
    }),
    acoes: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 1,
      marginTop: "2px",
    },
    espacador: {
      flexGrow: 1,
      "@media (max-width: 750px)": {
        display: "none",
      },
    },
    botao: {
      textTransform: "none",
      borderRadius: "8px",
      fontSize: "0.78rem",
      paddingInline: 1.5,
    },
    botaoRevogar: {
      textTransform: "none",
      borderRadius: "8px",
      fontSize: "0.78rem",
      paddingInline: 1.5,
      color: corTexto("REVOGADA"),
      borderColor: alpha(theme.palette.chip.main.pending, 0.5),
      "&:hover": {
        borderColor: theme.palette.chip.main.pending,
        backgroundColor: alpha(theme.palette.chip.main.pending, 0.08),
      },
    },
    botaoReativar: {
      textTransform: "none",
      borderRadius: "8px",
      fontSize: "0.78rem",
      paddingInline: 1.5,
      color: corTexto("ATIVA"),
      borderColor: alpha(theme.palette.chip.main.completed, 0.5),
      "&:hover": {
        borderColor: theme.palette.chip.main.completed,
        backgroundColor: alpha(theme.palette.chip.main.completed, 0.08),
      },
    },
    aviso: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 6,
    },
    avisoTexto: {
      color: theme.palette.text.secondary,
    },
  };
};

export default useStyles;
